/** tm/legal/LegalOverview.tsx — 개요 (아무것도 고르지 않았을 때 3열 · 모바일 첫 화면): 제목·설명 · 관련 조문·현행 원문 · 곧 시행(공포번호) · 새 소식 5건 · (전체 주제) 주제별 요약
 *  head: 3열 첫 줄 오른쪽 묶음(수집 상태·새 소식·설정)을 제목과 같은 줄에 놓는다. */
import type { ReactNode } from 'react';
import { C, T, R } from '../components/ui';
import { shortLaw, shortDate, effectiveLabel, num, secTitle, ddayTone, MUTED } from './legalTheme';
import LegalFeedCard from './LegalFeedCard';
import LegalPipeline from './LegalPipeline';
import { useOverviewData } from './useOverviewData';
import LegalTodaySummary from './LegalTodaySummary';
import type { FeedRow, FeedTab, ItemKind, LegalAlerts, Topic } from './legalTypes';

interface Props {
  topic: Topic | null; topics: Topic[]; tab: FeedTab; starred: boolean; onlyNew: boolean; alertsTotal: number; mobile?: boolean;
  onTab: (t: FeedTab) => void; onTopic: (label: string) => void; onOpen: (r: FeedRow) => void; onSelect: (kind: ItemKind, id: number) => void;
  /** 제목 줄 오른쪽 (데스크톱 도구 묶음) */
  head?: ReactNode;
  /** 「새 소식」 화면: 카드 대신 오늘 요약 (알림 묶음) */
  alerts?: LegalAlerts; onReadAll?: () => void;
  /** 모바일: 「전체 글 목록 보기」 */
  footer?: ReactNode;
}

const Sec = ({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) => (
  <section style={{ marginBottom: 20, maxWidth: 820 }}>
    <div style={{ ...secTitle, fontSize: T.md, marginTop: 0, display: 'flex', justifyContent: 'space-between' }}><span>{title}</span>{right}</div>
    {children}
  </section>
);
const muted = (t: string) => <div style={{ fontSize: T.md, color: MUTED, padding: '4px 2px' }}>{t}</div>;

export default function LegalOverview(p: Props) {
  const { topic, topics, tab, starred, onlyNew, alertsTotal, mobile, onOpen } = p;
  const label = topic?.label || '';
  const summary = onlyNew && !!p.alerts;
  const { fresh, soon } = useOverviewData(label, !starred && !summary);
  const title = starred ? '★ 별표' : onlyNew ? '새 소식' : topic ? topic.label : '전체 주제';
  const desc = starred ? '상세에서 ☆ 별표한 글이 모입니다. 목록에서 고르세요.'
    : onlyNew ? `확인하지 않은 새 글 ${num(alertsTotal)}건 (법령 개정·국회 법안·국무회의 의결·입법예고·판례·해석, 뉴스 제외) — 목록에서 열면 확인한 것으로 바뀝니다.`
      : topic ? topic.description : '업무 주제별로 법령 개정·판례·해석·국회 법안·입법예고·국무회의 의결·뉴스를 모아 두는 곳입니다. 새 글은 매일 새벽 4시에 모이고, 중요한 것은 「새 소식」으로 알려 드립니다.';
  const pins = topic?.pins || [];
  const interest = topics.filter(t => !t.feed);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
        <h2 style={{ fontSize: T.xl, fontWeight: 700, color: C.text, margin: 0, flex: 1, lineHeight: 1.4 }}>{title}</h2>
        {p.head}
      </div>
      {desc && <p style={{ fontSize: T.md, color: C.sub, margin: '0 0 14px', lineHeight: 1.65, maxWidth: 820 }}>{desc}{topic?.alert === false ? ' · 이 주제는 새 글 알림을 보내지 않습니다.' : ''}</p>}
      {!starred && <LegalPipeline stage={tab} onTab={p.onTab} />}
      {summary && p.alerts && <LegalTodaySummary data={p.alerts} onTab={p.onTab} onSelect={p.onSelect} onReadAll={p.onReadAll} />}

      {pins.length > 0 && (
        <Sec title="관련 조문 · 현행 원문" right={<span style={{ fontWeight: 400, color: MUTED }}>{pins.length}개 · 새 창</span>}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 160 : 210}px, 1fr))`, gap: 6 }}>
            {pins.map(pn => (
              <a key={`${pn.law}${pn.jo}${pn.title}`} href={pn.url} target="_blank" rel="noopener noreferrer" className="lw-f lw-card" title={`${pn.law} ${pn.jo} — 새 창에서 현행 원문`}
                style={{ display: 'block', padding: '9px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, textDecoration: 'none', color: C.text }}>
                <span style={{ display: 'block', fontSize: T.sm, color: MUTED, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortLaw(pn.law)} {pn.jo}</span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pn.title || '법령 전문'}</span>
                {pn.upcoming && <span style={{ display: 'block', fontSize: T.sm, color: C.accent, marginTop: 2 }}>개정 시행예정 {shortDate(pn.upcoming.effective_at)}</span>}
              </a>
            ))}
          </div>
        </Sec>
      )}

      {!onlyNew && !starred && (
        <Sec title="곧 시행">
          {soon === null ? muted('불러오는 중…') : soon.length === 0 ? muted('시행을 앞둔 법령 개정이 없습니다.') : soon.map(r => {
            const e = effectiveLabel(r.effective_at);
            return (
              <button key={r.key} type="button" className="lw-f lw-fi" onClick={() => p.onSelect('legal', r.id)} title={`${r.title} — 시행 ${shortDate(r.effective_at)}`} style={{
                display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 4px', borderRadius: R.sm, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <span style={{ width: 70, flexShrink: 0, fontSize: T.sm, fontWeight: 700, color: ddayTone(e?.days).fg, fontVariantNumeric: 'tabular-nums' }}>{e?.text}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                  <span style={{ display: 'block', fontSize: T.sm, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                    {r.nos.length ? `제${r.nos[0]}호` : '공포'}{r.promulgated ? ` · ${shortDate(r.promulgated)} 공포` : ''}{r.nos.length > 1 ? ` (같은 날 시행 공포 ${r.nos.length}건)` : ''}
                  </span>
                </span>
                <span style={{ fontSize: T.sm, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{shortDate(r.effective_at)}</span>
              </button>
            );
          })}
        </Sec>
      )}

      {!starred && !summary && (
        <Sec title="이번 주 새 소식">
          {fresh === null ? muted('불러오는 중…') : fresh.length === 0 ? muted('확인할 새 글이 없습니다. 매일 새벽 4시에 새로 모읍니다.')
            : fresh.map(r => <LegalFeedCard key={`${r.kind}${r.id}`} row={r} selected={false} onClick={() => onOpen(r)} />)}
        </Sec>
      )}

      {!topic && !onlyNew && !starred && interest.length > 0 && (
        <Sec title="주제별 요약">
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 150 : 220}px, 1fr))`, gap: 6 }}>
            {interest.map(t => {
              const nw = t.alert === false ? t.newCount ?? 0 : t.alerts;
              const next = t.pins.map(x => x.upcoming?.effective_at).filter((x): x is string => !!x).sort()[0];
              return (
                <button key={t.id} type="button" className="lw-f lw-card" onClick={() => p.onTopic(t.label)} title={`${t.label} — ${t.description || ''}`} style={{
                  textAlign: 'left', padding: '9px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: C.text, wordBreak: 'keep-all' }}>{t.label}</span>
                  <span style={{ display: 'block', fontSize: T.sm, color: C.sub, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    전체 {num(t.counts.all)}{nw > 0 && <b style={{ color: C.accent }}> · +{num(nw)} 새 글</b>}{next && !mobile ? ` · 시행예정 ${shortDate(next)}` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </Sec>
      )}
      {p.footer}
    </div>
  );
}
